import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  Grid,
  OutlinedInput,
  FormHelperText,
  InputLabel,
  Typography,
  Divider,
  Chip,
  Button,
  TextField,
} from "@mui/material";

import KeyIcon from "@mui/icons-material/Key";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
// import ArchiveIcon from "@mui/icons-material/Archive";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LockIcon from "@mui/icons-material/Lock";

import CustomSwitch from "../../shared/CustomOnOffSwitch";
import CustomSelect from "../../shared/CustomSelect";
import { DurationPicker } from "./DurationPicker";
import { calculateLeftDays } from "../../../utils/helperFunctions";
import { messageBands } from "../../../data";

import { getBanks } from "./state/actions";

const LicenseDetails = ({
  formik,
  modules,
  countries,
  banks,
  touched,
  setTouched,
  success,
  error,
}) => {
  const dispatch = useDispatch();
  const [bicOptions, setBicOptions] = useState(
    banks?.find((bank) => bank.id === formik.values.bankId)?.bics || []
  );

  const [selectedBank, setSelectedBank] = useState({
    id: "SELECT",
    value: "SELECT_BANK",
    label: "SELECT_BANK",
  });

  const [selectedBic, setSelectedBic] = useState({
    id: "SELECT",
    value: "SELECT_BANK_BIC",
    label: "SELECT_BANK_BIC",
  });

  const [selectedMessages, setSelectedMessages] = useState({
    id: "SELECT",
    value: "SELECT_MESSAGES_BAND",
    label: "SELECT_MESSAGES_BAND",
  });

  const [selectedCountry, setSelectedCountry] = useState({
    id: "SELECT",
    value: "SELECT_COUNTRY",
    label: "SELECT_COUNTRY",
  });

  const handleChange = (e) => {
    if (!touched) setTouched(true);
    formik.handleChange(e);
  };

  const handleIncreaseUsers = (e) => {
    if (!touched) setTouched(true);
    formik.setFieldValue("allowedUsers", formik.values.allowedUsers + 1);
  };

  const handleDecreaseUsers = (e) => {
    if (!touched) setTouched(true);
    formik.setFieldValue("allowedUsers", formik.values.allowedUsers - 1);
  };

  const handleChangeBankName = (newValue) => {
    const bank = banks.find((bank) => bank.id === newValue.id);
    formik.setFieldValue("bankShortName", bank.shortName);
    formik.setFieldValue("bankName", bank.name);
    formik.setFieldValue("bankId", bank.id);
    formik.setFieldValue("bankBic", "SELECT_BANK_BIC");
  };

  const handleChangeBankBic = (newValue) => {
    formik.setFieldValue("bankBic", newValue.value);
    formik.setFieldValue("bankBicId", newValue.id);
    setSelectedBic({
      id: newValue.id,
      value: newValue.value,
      label: newValue.label,
    });
  };

  const handleChangeMessages = (newValue) => {
    formik.setFieldValue("messages", newValue.value);
    setSelectedMessages({
      id: newValue.id,
      value: newValue.value,
      label: newValue.label,
    });
    if (!touched) setTouched(true);
  };

  const handleChangeCountry = (newValue) => {
    formik.setFieldValue("countryId", newValue.id);

    //reset bank and bank bic
    formik.setFieldValue("bankShortName", "SELECT_BANK");
    formik.setFieldValue("bankName", "SELECT_BANK");
    formik.setFieldValue("bankId", "SELECT_BANK");
    formik.setFieldValue("bankBic", "SELECT_BANK_BIC");
    formik.setFieldValue("bankBicId", "SELECT_BANK_BIC");

    //load banks
    dispatch(getBanks("", newValue.id, error));
  };

  const handleChangeModule = (e) => {
    const moduleId = e.target.id;
    const isAllowed = e.target.value === "false" ? false : true;
    const newModules = [...formik.values.modules].map((module) => {
      if (formik.values.isNew) {
        if (`${module.id}` === moduleId)
          return { ...module, isAllowed: !isAllowed };
        else return module;
      } else {
        if (`${module.module.id}` === moduleId)
          return {
            ...module,
            isAllowed: !isAllowed,
          };
        else return module;
      }
    });

    formik.setFieldValue("modules", newModules);

    if (!touched) setTouched(true);
  };

  const copyToClip = () => {
    navigator.clipboard.writeText(formik.values.key);
    success("copied!");
  };

  const calculateDaysLeft = useCallback(() => {
    return calculateLeftDays(
      new Date(formik.values.creationDate),
      new Date(formik.values.expiryDate)
    );
  }, [formik.values.creationDate, formik.values.expiryDate]);

  useEffect(() => {
    if (formik.values.bankId) {
      const bank = banks?.find((bank) => bank.id === formik.values.bankId);
      setBicOptions(bank?.bics || []);
      setSelectedBank({
        id:
          formik.values.bankId === "SELECT_BANK"
            ? "SELECT"
            : formik.values.bankId,
        value: formik.values.bankShortName,
        label: formik.values.bankShortName,
      });
    }
  }, [banks, formik]);

  useEffect(() => {
    if (formik.values.bankBic)
      setSelectedBic({
        id:
          formik.values.bankBicId === "SELECT_BANK_BIC"
            ? "SELECT"
            : formik.values.bankBicId,
        value: formik.values.bankBic,
        label: formik.values.bankBic,
      });
  }, [formik.values.bankBic, formik.values.bankBicId]);

  useEffect(() => {
    const country = countries.find(
      (coun) => coun.id === formik.values.countryId
    );
    setSelectedCountry({
      id: country?.id || "SELECT",
      value: country?.id || "SELECT_COUNTRY",
      label: country?.name || "SELECT_COUNTRY",
    });
  }, [countries, formik.values.countryId]);

  useEffect(() => {
    const band = messageBands.find(
      (messageBand) => messageBand.value === formik.values.messages
    );
    setSelectedMessages({
      id: band?.id || "SELECT",
      value: band?.value || "SELECT_MESSAGE_BAND",
      label: band?.label || "SELECT_MESSAGE_BAND",
    });
  }, [formik.values.messages]);
  // console.log(bicOptions);
  // console.log(formik.values.bankId);
  // console.log(banks);
  return (
    <Grid item container direction="column">
      {/*Main info */}

      {/*License status */}
      <Grid item container direction="column" p={2}>
        <Grid item>
          {!formik.values.isNew && formik.values.status !== "EXPIRED" && (
            <Chip
              color={formik.values.status === "RUNNING" ? "success" : "warning"}
              variant="outlined"
              label={formik.values.status}
              sx={{
                height: "20px",
              }}
            />
          )}
        </Grid>
        {/*License Name */}
        <Grid item container justifyContent="space-between">
          <Grid item xs={9}>
            <InputLabel
              htmlFor="licenseName"
              sx={{
                color: "#050D2E",
                fontSize: ".875rem",
                fontWeight: 550,
                letterSpacing: "-0.5px",
                lineHeight: "16px",
                pb: 1,
                pt: 1,
              }}
            >
              License Name
            </InputLabel>

            <OutlinedInput
              id="licenseName"
              sx={{
                width: "360px",
                height: "36px",
                bgcolor: "#ffffff",
              }}
              value={formik.values.licenseName}
              onChange={handleChange}
              error={Boolean(formik.errors.licenseName)}
              disabled={!formik.values.isNew}
            />
            {Boolean(formik.errors.licenseName) && (
              <FormHelperText id="licenseName" error={true}>
                {formik.errors.licenseName}
              </FormHelperText>
            )}
          </Grid>
          <Grid item>
            {!formik.values.isNew && (
              <Chip
                label={
                  formik.values.status === "EXPIRED"
                    ? "Expired"
                    : `${calculateDaysLeft()} Days Left`
                }
                sx={{
                  height: "20px",
                  backgroundColor:
                    formik.values.status === "EXPIRED" ? "#FFD8D6" : "#E7E9EF",
                  color:
                    formik.values.status === "EXPIRED" ? "#400F0C" : "#050D2E",
                }}
              />
            )}
          </Grid>
        </Grid>

        {/*country */}
        <Grid item>
          <InputLabel
            htmlFor="countryId"
            sx={{
              color: "#050D2E",
              fontSize: ".875rem",
              fontWeight: 550,
              letterSpacing: "-0.5px",
              lineHeight: "16px",
              pb: 1,
              pt: 1,
            }}
          >
            Country
          </InputLabel>

          <CustomSelect
            options={[
              ...countries.map((country) => ({
                value: country.id,
                label: country.name,
                id: country.id,
              })),
              {
                value: "SELECT_COUNTRY",
                label: "SELECT_COUNTRY",
                id: "SELECT",
              },
            ]}
            selectedValue={selectedCountry}
            handleChangeSelection={handleChangeCountry}
            groupBy="Countries"
            width={360}
            disabled={formik.values.isNew ? false : true}
            // disabled={false}
          />
          {Boolean(formik.errors.countryId) && (
            <FormHelperText id="countryId" error={true}>
              {formik.errors.countryId}
            </FormHelperText>
          )}
        </Grid>

        {/*Bank Name */}
        <Grid item>
          <InputLabel
            htmlFor="bankName"
            sx={{
              color: "#050D2E",
              fontSize: ".875rem",
              fontWeight: 550,
              letterSpacing: "-0.5px",
              lineHeight: "16px",
              pb: 1,
              pt: 1,
            }}
          >
            Bank Name
          </InputLabel>

          <CustomSelect
            options={[
              ...banks?.map((bank) => ({
                value: bank.shortName,
                label: bank.shortName,
                id: bank.id,
              })),
              { value: "SELECT_BANK", label: "SELECT_BANK", id: "SELECT" },
            ]}
            selectedValue={selectedBank}
            handleChangeSelection={handleChangeBankName}
            groupBy="Banks"
            width={360}
            disabled={formik.values.isNew ? false : true}
            // disabled={false}
          />
          {Boolean(formik.errors.bankShortName) && (
            <FormHelperText id="bankShortName" error={true}>
              {formik.errors.bankShortName}
            </FormHelperText>
          )}
        </Grid>

        {/*Bank BIC */}
        <Grid item>
          <InputLabel
            htmlFor="bankBic"
            sx={{
              color: "#050D2E",
              fontSize: ".875rem",
              fontWeight: 550,
              letterSpacing: "-0.5px",
              lineHeight: "16px",
              pb: 1,
              pt: 1,
            }}
          >
            BIC
          </InputLabel>
          {/* <OutlinedInput
            id="bankBic"
            sx={{
              width: "360px",
              height: "36px",
              bgcolor: "#ffffff",
              "& .MuiInputBase-root": { height: "36px" },
              "& .MuiOutlinedInput": { height: "36px" },
            }}
            value={formik.values.bankBic}
            onChange={handleChange}
            error={Boolean(formik.errors.bankBic)}
            disabled
          /> */}

          <CustomSelect
            // fieldName="Bank Name"
            options={[
              ...bicOptions.map((bic) => ({
                value: bic.value,
                label: bic.value,
                id: bic.id,
              })),
              {
                value: "SELECT_BANK_BIC",
                label: "SELECT_BANK_BIC",
                id: "SELECT",
              },
            ]}
            selectedValue={selectedBic}
            handleChangeSelection={handleChangeBankBic}
            groupBy="BICs"
            width={360}
            disabled={formik.values.isNew ? false : true}
            // disabled={false}
          />
          {Boolean(formik.errors.bankBic) && (
            <FormHelperText id="bankBic" error={true}>
              {formik.errors.bankBic}
            </FormHelperText>
          )}
        </Grid>

        {/*License Key */}
        <Grid item container alignItems="center" sx={{ marginTop: "20px" }}>
          <KeyIcon sx={{ color: "#050D2E", width: "15px" }} />
          <Typography
            sx={{
              color: "#050D2E",
              fontSize: ".938rem",
              letterSpacing: "-0.5px",
              lineHeight: "18px",
              paddingLeft: "5px",
              fontWeight: 500,
            }}
          >
            License Short Key
          </Typography>
        </Grid>
        <Grid item container alignItems="center">
          <Chip
            label={`${formik.values.keySimplified}`}
            sx={{
              height: "20px",
              borderRadius: "2px",
              backgroundColor: "#E7E9EF",
              mr: 2,
            }}
          />
          <ContentCopyIcon
            sx={{ color: "#050D2E", width: "15px", pr: 2, cursor: "pointer" }}
            onClick={copyToClip}
          />
          {/* <ArchiveIcon sx={{ color: "#050D2E", width: "15px", pr: 2 }} /> */}
        </Grid>

        <TextField
          id="full-key"
          name="full-key"
          multiline
          fullWidth
          value={formik.values.key}
          sx={{
            backgroundColor: "#ffffff",
            my: 2,
          }}
          disabled
        />
        {/* <Grid item container>
          {formik.values.key.match(/.{1,30}/g)?.map((part) => {
            return <span key={part}>{part}</span>;
          })}
        </Grid> */}

        {!formik.values.isNew ? (
          <>
            <Grid item container alignItems="center" sx={{ marginTop: "20px" }}>
              <KeyIcon sx={{ color: "#050D2E", width: "15px" }} />
              <Typography
                sx={{
                  color: "#050D2E",
                  fontSize: ".938rem",
                  letterSpacing: "-0.5px",
                  lineHeight: "18px",
                  paddingLeft: "5px",
                  fontWeight: 500,
                }}
              >
                License Key
              </Typography>
            </Grid>
            <Grid item container alignItems="center">
              <Chip
                label={`${formik.values.uniqueId}`}
                sx={{
                  height: "20px",
                  borderRadius: "2px",
                  backgroundColor: "#E7E9EF",
                  mr: 2,
                }}
              />
            </Grid>
          </>
        ) : null}
      </Grid>

      <Divider />

      {/*Info */}
      <Grid
        item
        container
        direction="column"
        p={2}
        sx={{ backgroundColor: "#ffffff" }}
      >
        {/*License Duration */}
        <Grid item>
          <InputLabel
            htmlFor="duration"
            sx={{
              color: "#050D2E",
              fontSize: ".875rem",
              fontWeight: 550,
              letterSpacing: "-0.5px",
              lineHeight: "16px",
              pb: 1,
              pt: 1,
            }}
          >
            License Duration
          </InputLabel>
          <DurationPicker formik={formik} setTouched={setTouched} />
          <FormHelperText
            id="duration"
            sx={{
              color: "#8792AB",
              fontSize: ".875rem",
              letterSpacing: "-0.47px",
              lineHeight: "20px",
              pt: 2,
              pb: 2,
            }}
          >
            License Expiration Date: {formik.values.formatedExpiryDate}
          </FormHelperText>
        </Grid>

        {/*Allowed Users */}
        <Grid item>
          <InputLabel
            htmlFor="allowedUsers"
            sx={{
              color: "#050D2E",
              fontSize: ".875rem",
              fontWeight: 550,
              letterSpacing: "-0.5px",
              lineHeight: "16px",
              pb: 1,
              pt: 1,
            }}
          >
            Concurrent Users Number
          </InputLabel>
          <Grid container sx={{ width: "112px", height: "34px" }}>
            <Grid item xs={3}>
              <Button
                variant="contained"
                disableElevation
                sx={{
                  height: "34px",
                  width: "100%",
                  minWidth: "0px",
                  p: 0,
                  border: "1px solid #DBDEE6",
                  borderRadius: "4px 0 0 4px",
                  backgroundColor: "#E7E9EF",
                  color: "#50566E",
                  "&:hover": {
                    backgroundColor: "#E7E9EF",
                  },
                }}
                onClick={handleDecreaseUsers}
                disabled={formik.values.status === "EXPIRED"}
              >
                <RemoveIcon />
              </Button>
            </Grid>
            <Grid item xs={5}>
              <OutlinedInput
                id="allowedUsers"
                sx={{ maxHeight: "34px", borderRadius: 0 }}
                error={Boolean(formik.errors.allowedUsers)}
                value={formik.values.allowedUsers}
                onChange={formik.handleChange}
                disabled={formik.values.status === "EXPIRED"}
              />
            </Grid>
            <Grid item xs={3}>
              <Button
                variant="contained"
                disableElevation
                sx={{
                  height: "34px",
                  width: "100%",
                  minWidth: "0px",
                  p: 0,
                  border: "1px solid #DBDEE6",
                  borderRadius: "0 4px 4px 0",
                  backgroundColor: "#E7E9EF",
                  color: "#50566E",
                  "&:hover": {
                    backgroundColor: "#E7E9EF",
                  },
                }}
                onClick={handleIncreaseUsers}
                disabled={formik.values.status === "EXPIRED"}
              >
                <AddIcon />
              </Button>
            </Grid>
          </Grid>

          {Boolean(formik.errors.allowedUsers) && (
            <FormHelperText id="allowedUsers" error={true}>
              {formik.errors.allowedUsers}
            </FormHelperText>
          )}
        </Grid>

        {/*Messages */}
        <Grid item>
          <InputLabel
            htmlFor="messages"
            sx={{
              color: "#050D2E",
              fontSize: ".875rem",
              fontWeight: 550,
              letterSpacing: "-0.5px",
              lineHeight: "16px",
              pb: 1,
              pt: 2,
            }}
          >
            Messages/Day Volume
          </InputLabel>
          <CustomSelect
            options={[
              ...messageBands.map((band) => ({
                value: band.value,
                label: band.label,
                id: band.id,
              })),
              {
                value: "SELECT_MESSAGE_BAND",
                label: "SELECT_MESSAGE_BAND",
                id: "SELECT",
              },
            ]}
            selectedValue={selectedMessages}
            handleChangeSelection={handleChangeMessages}
            groupBy="Message Bands"
            width={360}
            disabled={formik.values.status === "EXPIRED"}
          />
          {/* <OutlinedInput
            id="messages"
            sx={{
              width: "360px",
              height: "36px",
              "& .MuiInputBase-root": { height: "36px" },
              "& .MuiOutlinedInput": { height: "36px" },
            }}
            type="number"
            value={formik.values.messages}
            onChange={handleChange}
            error={Boolean(formik.errors.messages)}
            disabled={formik.values.status === "EXPIRED"}
          /> */}
          {Boolean(formik.errors.messages) && (
            <FormHelperText id="messages" error={true}>
              {formik.errors.messages}
            </FormHelperText>
          )}
        </Grid>
      </Grid>

      <Divider />

      {/*Modules */}
      <Grid
        item
        container
        direction="column"
        p={2}
        sx={{ backgroundColor: "#ffffff" }}
      >
        <Typography
          sx={{
            color: "#050D2E",
            fontSize: "1rem",
            fontWeight: 550,
            letterSpacing: "-0.53px",
            lineHeight: "19px",
            pb: 2,
            pt: 2,
          }}
        >
          Modules Settings
        </Typography>
        <Typography
          sx={{
            color: "#8792AB",
            fontSize: ".875rem",
            fontWeight: 500,
            lineHeight: "20px",
            pb: 3,
          }}
        >
          Set license active and inactive modules
        </Typography>
        {modules.map((module, i) => {
          return (
            <div key={formik.values.isNew ? module.id : module.module.id}>
              <Grid container>
                <Grid item xs={9}>
                  <Typography
                    sx={{
                      color: "#050D2E",
                      fontSize: ".875rem",
                      fontWeight: 500,
                      letterSpacing: "-0.53px",
                      lineHeight: "19px",
                      pb: 1,
                    }}
                  >
                    {formik.values.isNew ? module.name : module.module.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#8792AB",
                      fontSize: ".875rem",
                      fontWeight: 500,
                      lineHeight: "20px",
                      pb: 2,
                    }}
                  >
                    {formik.values.isNew
                      ? module.description
                      : module.module.description}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{ display: "flex", justifyContent: "end" }}
                >
                  {formik.values.isNew ? (
                    module.isLocked ? (
                      <LockIcon sx={{ color: "grey", mt: 1 }} />
                    ) : null
                  ) : module.module.isLocked ? (
                    <LockIcon sx={{ color: "grey", mt: 1 }} />
                  ) : null}
                  <CustomSwitch
                    id={
                      formik.values.isNew
                        ? `${module.id}`
                        : `${module.module.id}`
                    }
                    checked={module.isAllowed || false}
                    value={module.isAllowed || false}
                    disabled={
                      (formik.values.isNew
                        ? module.isLocked
                        : module.module.isLocked) ||
                      formik.values.status === "EXPIRED"
                    }
                    onChange={handleChangeModule}
                  />
                </Grid>
              </Grid>
              {i !== modules.length - 1 && <Divider sx={{ mb: 2 }} />}
            </div>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default LicenseDetails;
